import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch } from 'redux/storeHooks';
import { getSchedulingStatus } from 'redux/slices/messagesSlice.slice';
import { DateTime } from 'luxon';

interface SchedulingStatus {
  scheduledCount: number;
  scheduledLimit: number;
  sendingCount: number;
  sendingLimit: number;
  canSchedule: boolean;
  canSend: boolean;
}

interface UseSchedulingStatusReturn {
  schedulingStatus: SchedulingStatus | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useSchedulingStatus = (date: Date): UseSchedulingStatusReturn => {
  const dispatch = useAppDispatch();
  const [schedulingStatus, setSchedulingStatus] =
    useState<SchedulingStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSchedulingStatus = useCallback(async () => {
    if (!date) return;

    const dateString = DateTime.fromJSDate(date).toISODate();

    setIsLoading(true);
    setError(null);

    try {
      const result = await dispatch(
        getSchedulingStatus({ params: { date: dateString } })
      );

      if (getSchedulingStatus.fulfilled.match(result)) {
        setSchedulingStatus(result.payload);
      } else if (getSchedulingStatus.rejected.match(result)) {
        const rejectedPayload = result.payload as { message?: string } | undefined;
        setError(
          rejectedPayload?.message ??
            result.error.message ??
            'Failed to fetch scheduling status'
        );
      } else {
        setError('Failed to fetch scheduling status');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to fetch scheduling status';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, date]);

  useEffect(() => {
    void fetchSchedulingStatus();
  }, [fetchSchedulingStatus]);

  return {
    schedulingStatus,
    isLoading,
    error,
    refetch: fetchSchedulingStatus,
  };
};
